/* eslint-disable no-empty,no-unused-vars */
/* global PLATFORM, G */
import { curry, getFirstItem, setKey } from '@gaia/util'
import appSettings from 'config/app.json5'

/**
 * Handler for the proxy that wraps `meta.acl`.
 * @type {{get(*, *, *): number[]|*}}
 * @private
 */
export const _aclProxyHandler = {
  get(target, prop, receiver) {
    return Object.keys(target).includes('*')
      ? [10, 20, 30, 40]
      : target?.[prop]
  },
}

/**
 * Initializes the application context by setting it from:
 *  1. the default context of the current User, if they have one; or
 *  2. a random context from among those available to the user.
 *
 *  The name of the context is set for the starting route (if it doesn't already have one) and also
 *  for the whole application by storing it within `app[G.SESSION][G.STATE][G.CONTEXT]`, which is
 *  also accessible by the application's modules through the `module[G.ADAPTER][G.ROUTER].context()`
 *  method.
 *
 *  NOTE: We now allow `null` contexts instead of randomly picking one as a fallback. This will be
 *  caught and redirected to `error/noContext`
 *
 * @type {(function(...[*]): (*))|*}
 */
const init = curry(async (obj, args) => {
  try {
    const settingsAdapter = obj[G.ADAPTER][G.SETTINGS]
    const meta = obj[G.ADAPTER][G.SESSION][G.STATE][G.META]
    const settingsInitialized = !!settingsAdapter?.[G.STATE]?.key

    // Depending on how we got here, settings adapter may not be initialized yet
    // so use meta value as fallback
    const lastContext = settingsInitialized
      ? settingsAdapter[G.DATA]?.lastContext
      : getFirstItem(meta?.refs?.settings)?.value?.lastContext

    const { context: routeContext } = obj[G.ADAPTER][G.ROUTER][G.API].path()
    const contexts = meta.context || {}
    let newContext

    if (PLATFORM === 'web') {
      newContext = (contexts[routeContext] && routeContext)
        || (contexts[lastContext] && lastContext)
        || Object?.keys?.(contexts)?.[0]
    }

    if (PLATFORM === 'mobile') {
      const applicableContexts = Object
        .keys(contexts)
        .filter(x => appSettings.defaults.mobileContexts.includes(x))

      newContext = (applicableContexts.includes(routeContext) && routeContext)
        || (applicableContexts.includes(lastContext) && lastContext)
        || Object?.keys?.(contexts)?.find(c => applicableContexts.includes(c))
        || Object?.keys?.(contexts)?.[0]
    }

    !newContext && console.error('ATTENTION!!! Could not determine context')

    if (settingsInitialized && lastContext !== newContext) {
      settingsAdapter[G.API].set({ key: 'lastContext', value: newContext })
    }

    // setting user's acls according to active context
    meta.acl = new Proxy(contexts[newContext].acl, _aclProxyHandler)
    meta.roles = contexts[newContext].roles // setting user's acls according to active context
    setKey(newContext, G.CONTEXT, obj[G.SESSION][G.STATE]) // global context location
  } catch (ignored) {} // We keep the default route coming from route init

  return args
})

const contextInit = curry(init)

export default contextInit
